import { useState } from 'react';
import {
  Alert, Button, Dialog, DialogActions, DialogContent, DialogTitle, InputAdornment, TextField,
  ToggleButton, ToggleButtonGroup, Typography,
} from '@mui/material';
import api from '../services/api';
import { safeDecode } from '../utils/decode';
import { fieldErrors, wranglerError } from './errors';

/**
 * Wrangler's rule for a percentage increase (its DESIGN.md 9.6): round half up of
 * current limit × percent ÷ 100, and at least 1.
 */
export const percentIncrease = (currentLimit, percent) => {
  const exact = (currentLimit * percent) / 100;
  return Math.max(1, Math.floor(exact + 0.5));
};

const errorText = (err) => [wranglerError(err), ...fieldErrors(err)].join('\n');

/** Increase a quota's limit by a number or a percentage; the reply is applied, never retried. */
export const IncreaseDialog = ({ surveyId, quota, onClose, onDone }) => {
  const [mode, setMode] = useState('amount');
  const [value, setValue] = useState('');
  const [pending, setPending] = useState(false);
  const [error, setError] = useState(null);

  const number = Number(value);
  const valid = value !== '' && Number.isFinite(number) && number > 0 && (mode === 'percent' || Number.isInteger(number));
  const amount = valid ? (mode === 'amount' ? number : percentIncrease(quota.current_limit, number)) : null;

  const submit = async () => {
    setPending(true);
    setError(null);
    try {
      const body = mode === 'amount' ? { amount: number } : { percent: String(value) };
      const { data } = await api.post(`/surveys/${surveyId}/wrangler/quotas/${quota.id}/increase/`, body);
      onDone(data);
    } catch (err) {
      setError(errorText(err));
      setPending(false);
    }
  };

  return (
    <Dialog open onClose={pending ? undefined : onClose} maxWidth="xs" fullWidth>
      <DialogTitle>Increase {safeDecode(quota.name)}</DialogTitle>
      <DialogContent>
        <ToggleButtonGroup size="small" exclusive value={mode} onChange={(e, v) => v && setMode(v)} sx={{ mb: 2 }}>
          <ToggleButton value="amount" sx={{ textTransform: 'none' }}>By a number</ToggleButton>
          <ToggleButton value="percent" sx={{ textTransform: 'none' }}>By a percentage</ToggleButton>
        </ToggleButtonGroup>
        <TextField
          autoFocus fullWidth size="small" type="number" label={mode === 'amount' ? 'Add' : 'Increase by'}
          value={value} onChange={(e) => setValue(e.target.value)}
          InputProps={mode === 'percent' ? { endAdornment: <InputAdornment position="end">%</InputAdornment> } : undefined}
        />
        <Typography variant="body2" sx={{ mt: 1.5 }}>
          {amount
            ? <>Limit {quota.current_limit} → <strong>{quota.current_limit + amount}</strong> (+{amount})</>
            : `The limit is ${quota.current_limit}.`}
        </Typography>
        {mode === 'percent' && (
          <Typography variant="caption" color="text.secondary">
            A percentage of the current limit, rounded to the nearest whole number (halves up), at least 1.
          </Typography>
        )}
        {error && <Alert severity="error" sx={{ mt: 2, whiteSpace: 'pre-line' }}>{error}</Alert>}
      </DialogContent>
      <DialogActions>
        <Button onClick={onClose} disabled={pending} sx={{ textTransform: 'none' }}>Cancel</Button>
        <Button variant="contained" onClick={submit} disabled={!valid || pending} sx={{ textTransform: 'none' }}>
          {pending ? 'Increasing…' : 'Increase'}
        </Button>
      </DialogActions>
    </Dialog>
  );
};

/** Correct a quota's limit to an exact value, higher or lower; it's recorded in the history. */
export const SetLimitDialog = ({ surveyId, quota, onClose, onDone }) => {
  const [value, setValue] = useState(String(quota.current_limit));
  const [pending, setPending] = useState(false);
  const [error, setError] = useState(null);

  const limit = Number(value);
  const valid = value !== '' && Number.isInteger(limit) && limit >= 0;
  const unchanged = valid && limit === quota.current_limit;
  const closesAtOnce = valid && !unchanged && limit <= quota.complete_count;

  const submit = async () => {
    setPending(true);
    setError(null);
    try {
      const { data } = await api.post(`/surveys/${surveyId}/wrangler/quotas/${quota.id}/set-limit/`, { limit });
      onDone(data);
    } catch (err) {
      setError(errorText(err));
      setPending(false);
    }
  };

  return (
    <Dialog open onClose={pending ? undefined : onClose} maxWidth="xs" fullWidth>
      <DialogTitle>Set the limit of {safeDecode(quota.name)}</DialogTitle>
      <DialogContent>
        <TextField autoFocus fullWidth size="small" type="number" label="New limit" value={value}
          onChange={(e) => setValue(e.target.value)} sx={{ mt: 1 }}
          helperText={`Now ${quota.current_limit}; the original was ${quota.original_limit}. Higher or lower; the change is recorded.`} />
        {unchanged && <Typography variant="body2" color="text.secondary" sx={{ mt: 1 }}>That&apos;s the limit it has.</Typography>}
        {closesAtOnce && (
          <Alert severity="warning" sx={{ mt: 2 }}>
            It already has {quota.complete_count} completes, so it will close at once and screen out everyone it describes.
          </Alert>
        )}
        {error && <Alert severity="error" sx={{ mt: 2, whiteSpace: 'pre-line' }}>{error}</Alert>}
      </DialogContent>
      <DialogActions>
        <Button onClick={onClose} disabled={pending} sx={{ textTransform: 'none' }}>Cancel</Button>
        <Button variant="contained" onClick={submit} disabled={!valid || unchanged || pending} sx={{ textTransform: 'none' }}>
          {pending ? 'Saving…' : 'Set limit'}
        </Button>
      </DialogActions>
    </Dialog>
  );
};
